/**
 * Helper for building the language switcher items of header and footer.
 */
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import config from '@plone/volto/registry';
import type { LanguageItemProps } from '@designsystem/stories/04-organisms/Header';
import {
  normalizeContent,
  normalizeLink,
} from '@fhnw/helpers/DesignSystem/normalizeContent';

/** Content type of the per-language root folder (`/de`, `/en`, `/fr`). */
const LANGUAGE_ROOT_TYPE = 'LRF';

export interface LanguageItem {
  ['@id']: string;
  language: string;
}

export interface LanguageContent extends Omit<Content, '@components'> {
  '@components'?: {
    translations?: { items: LanguageItem[] };
  };
}

export interface BuildLanguageItemsOptions {
  /** Put the current language first instead of sorting it in alphabetically. */
  activeFirst?: boolean;
}

/**
 * The language root folders are translations of each other, so
 * plone.app.multilingual reports every language as available on a homepage —
 * even for a language that has no homepage of its own. `settings
 * .languagesHiddenOnLanguageRoot` lists the languages that must not be offered
 * there.
 */
const getHiddenLanguages = (): string[] =>
  (config.settings as Record<string, any>)?.languagesHiddenOnLanguageRoot || [];

/**
 * Builds the language switcher items for a piece of content.
 *
 * @param content - Content from Plone, including its translations
 * @param options - Ordering of the current language
 * @returns Array of language items
 */
export const buildLanguageItems = (
  content?: LanguageContent,
  { activeFirst = false }: BuildLanguageItemsOptions = {},
): LanguageItemProps[] => {
  const hidden =
    content?.['@type'] === LANGUAGE_ROOT_TYPE ? getHiddenLanguages() : [];

  const languages: LanguageItemProps[] = (
    content?.['@components']?.translations?.items || []
  )
    .filter((translation) => !hidden.includes(translation.language))
    .map((translation) => ({
      label: translation.language.toUpperCase(),
      href: normalizeLink(translation),
    }));

  // The current language is not part of its own translations.
  if (content?.language) {
    languages.unshift({
      label: String(normalizeContent(content.language, false)).toUpperCase(),
      href: normalizeLink(content),
      active: true,
    });
  }

  const byLabel = (a: LanguageItemProps, b: LanguageItemProps) =>
    a.label.localeCompare(b.label);

  if (!activeFirst) {
    return languages.sort(byLabel);
  }

  const active = languages.find((language) => language.active);
  const others = languages.filter((language) => !language.active).sort(byLabel);
  return active ? [active, ...others] : others;
};
